import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
/******************************************* */

const CustomTooltip = ({
	label,
	children,
	hasArrow = true,
	openDelay = 100,
	closeDelay = 500,
	hoverCloseDelay = 500,
	placement = 'top',
	className = '',
	disabled = false,
	variant = 'default',
	size = 'md',
	trigger = 'auto', // 'auto', 'hover', 'click', 'focus'
	persistent = false, // If true, only closes on outside click or ESC
	// Opt-in layout escape hatch. The wrapper is `inline-block` by default,
	// which shrink-wraps its child — correct for icons and short labels, but it
	// collapses anything meant to fill a grid cell or flex row (a row whose
	// value is pushed right with `ml="auto"` loses its alignment entirely).
	// `fullWidth` makes the wrapper a block that fills its parent instead.
	// Defaults to false, so all existing call sites are unaffected.
	fullWidth = false,
	...props
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [isMobile, setIsMobile] = useState(false);
	const [actualPlacement, setActualPlacement] = useState(placement);
	const [interactionType, setInteractionType] = useState(null); // 'hover' | 'click' | 'focus'

	const tooltipRef = useRef(null);
	const triggerRef = useRef(null);
	const openTimeoutRef = useRef(null);
	const closeTimeoutRef = useRef(null);
	const hoverTimeoutRef = useRef(null);

	// Detect mobile devices and touch capability
	useEffect(() => {
		const checkMobile = () => {
			const isTouchDevice =
				'ontouchstart' in window || navigator.maxTouchPoints > 0;
			const isSmallScreen = window.innerWidth <= 768;
			setIsMobile(isTouchDevice || isSmallScreen);
		};

		checkMobile();
		window.addEventListener('resize', checkMobile);
		return () => window.removeEventListener('resize', checkMobile);
	}, []);

	// Clear all timeouts
	const clearAllTimeouts = useCallback(() => {
		[openTimeoutRef, closeTimeoutRef, hoverTimeoutRef].forEach((ref) => {
			if (ref.current) {
				clearTimeout(ref.current);
				ref.current = null;
			}
		});
	}, []);

	// Enhanced close tooltip function
	const closeTooltip = useCallback(
		(immediate = false) => {
			clearAllTimeouts();

			if (immediate || persistent) {
				setIsOpen(false);
				setInteractionType(null);
				return;
			}

			// Use different delays based on interaction type
			const delay = interactionType === 'hover' ? hoverCloseDelay : closeDelay;

			closeTimeoutRef.current = setTimeout(() => {
				setIsOpen(false);
				setInteractionType(null);
			}, delay);
		},
		[closeDelay, hoverCloseDelay, persistent, interactionType]
	);

	// Open tooltip function
	const openTooltip = useCallback(
		(type, immediate = false) => {
			if (disabled) return;

			clearAllTimeouts();
			setInteractionType(type);

			if (immediate) {
				setIsOpen(true);
				return;
			}

			openTimeoutRef.current = setTimeout(() => {
				setIsOpen(true);
			}, openDelay);
		},
		[disabled, openDelay]
	);

	// Handle click outside and escape key
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				isOpen &&
				tooltipRef.current &&
				!tooltipRef.current.contains(event.target) &&
				triggerRef.current &&
				!triggerRef.current.contains(event.target)
			) {
				closeTooltip(true);
			}
		};

		const handleEscapeKey = (event) => {
			if (event.key === 'Escape' && isOpen) {
				closeTooltip(true);
			}
		};

		const handleScroll = () => {
			if (isOpen && !persistent) {
				closeTooltip(true);
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
			document.addEventListener('keydown', handleEscapeKey);
			window.addEventListener('scroll', handleScroll, true);

			return () => {
				document.removeEventListener('mousedown', handleClickOutside);
				document.removeEventListener('keydown', handleEscapeKey);
				window.removeEventListener('scroll', handleScroll, true);
			};
		}
	}, [isOpen, closeTooltip, persistent]);

	// Auto-close tooltip after a certain time for click interactions
	useEffect(() => {
		if (isOpen && interactionType === 'click' && !persistent) {
			const autoCloseTimeout = setTimeout(() => {
				closeTooltip(true);
			}, 3000); // Auto close after 3 seconds for click interactions

			return () => clearTimeout(autoCloseTimeout);
		}
	}, [isOpen, interactionType, persistent, closeTooltip]);

	// Position tooltip with collision detection
	useEffect(() => {
		if (isOpen && tooltipRef.current && triggerRef.current) {
			const tooltip = tooltipRef.current;
			const trigger = triggerRef.current;
			const triggerRect = trigger.getBoundingClientRect();
			const tooltipRect = tooltip.getBoundingClientRect();
			const viewport = {
				width: window.innerWidth,
				height: window.innerHeight,
			};

			const offset = 12;
			const arrowSize = hasArrow ? 8 : 0;

			// Calculate positions for all placements
			const positions = {
				top: {
					top: triggerRect.top - tooltipRect.height - offset - arrowSize,
					left:
						triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2,
				},
				bottom: {
					top: triggerRect.bottom + offset + arrowSize,
					left:
						triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2,
				},
				left: {
					top:
						triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2,
					left: triggerRect.left - tooltipRect.width - offset - arrowSize,
				},
				right: {
					top:
						triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2,
					left: triggerRect.right + offset + arrowSize,
				},
			};

			// Collision detection
			let bestPlacement = placement;
			let position = positions[placement];

			const isOutOfBounds = (pos) => {
				return (
					pos.top < 8 ||
					pos.left < 8 ||
					pos.top + tooltipRect.height > viewport.height - 8 ||
					pos.left + tooltipRect.width > viewport.width - 8
				);
			};

			if (isOutOfBounds(position)) {
				const alternatives = {
					top: ['bottom', 'right', 'left'],
					bottom: ['top', 'right', 'left'],
					left: ['right', 'top', 'bottom'],
					right: ['left', 'top', 'bottom'],
				};

				for (const alt of alternatives[placement]) {
					if (!isOutOfBounds(positions[alt])) {
						bestPlacement = alt;
						position = positions[alt];
						break;
					}
				}
			}

			// Constrain to viewport
			position.left = Math.max(
				8,
				Math.min(position.left, viewport.width - tooltipRect.width - 8)
			);
			position.top = Math.max(
				8,
				Math.min(position.top, viewport.height - tooltipRect.height - 8)
			);

			tooltip.style.top = `${position.top}px`;
			tooltip.style.left = `${position.left}px`;
			setActualPlacement(bestPlacement);
		}
	}, [isOpen, placement, hasArrow]);

	// Determine effective trigger based on device and settings
	const getEffectiveTrigger = () => {
		if (trigger !== 'auto') return trigger;
		return isMobile ? 'click' : 'hover';
	};

	const effectiveTrigger = getEffectiveTrigger();

	// Event handlers
	const handleMouseEnter = () => {
		if (effectiveTrigger === 'hover' || effectiveTrigger === 'focus') {
			openTooltip('hover');
		}
	};

	const handleMouseLeave = () => {
		if (effectiveTrigger === 'hover' && interactionType === 'hover') {
			closeTooltip();
		}
	};

	const handleClick = (e) => {
		e.preventDefault();
		e.stopPropagation();

		if (effectiveTrigger === 'click' || isMobile) {
			if (isOpen && interactionType === 'click') {
				closeTooltip(true);
			} else {
				openTooltip('click', true);
			}
		}
	};

	const handleFocus = () => {
		if (effectiveTrigger === 'focus') {
			openTooltip('focus', true);
		}
	};

	const handleBlur = () => {
		if (effectiveTrigger === 'focus' && interactionType === 'focus') {
			closeTooltip();
		}
	};

	const handleTooltipMouseEnter = () => {
		// Keep tooltip open when hovering over it
		if (interactionType === 'hover') {
			clearAllTimeouts();
		}
	};

	const handleTooltipMouseLeave = () => {
		// Close tooltip when mouse leaves tooltip area
		if (interactionType === 'hover') {
			closeTooltip();
		}
	};

	// Cleanup on unmount
	useEffect(() => {
		return () => clearAllTimeouts();
	}, [clearAllTimeouts]);

	return (
		<>
			<style>{`
				.tooltip-container {
					position: relative;
					display: inline-block;
				}

				.tooltip-trigger {
					display: inline-flex;
					cursor: ${
						effectiveTrigger === 'click'
							? 'pointer'
							: effectiveTrigger === 'hover'
								? 'help'
								: 'default'
					};
					transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
					outline: none;
				}

				.tooltip-trigger:hover:not(.disabled) {
					transform: translateY(-1px);
				}

				/* fullWidth: fill the parent instead of shrink-wrapping. The
				   hover lift is dropped too — a full-width row nested inside a
				   card that already lifts on hover reads as a glitch. */
				.tooltip-container.full-width,
				.tooltip-trigger.full-width {
					display: block;
					width: 100%;
				}

				.tooltip-trigger.full-width:hover:not(.disabled) {
					transform: none;
				}

				.tooltip-trigger:focus-visible {
					outline: 2px solid var(--chakra-colors-accent-gold);
					outline-offset: 2px;
					border-radius: 4px;
				}

				.tooltip-trigger.disabled {
					cursor: not-allowed;
					opacity: 0.6;
				}

				.tooltip-base {
					position: fixed;
					z-index: 9999;
					font-weight: 500;
					border-radius: 8px;
					box-shadow:
						0 10px 25px rgba(0, 0, 0, 0.1),
						0 4px 6px rgba(0, 0, 0, 0.05);
					backdrop-filter: blur(10px);
					border: 1px solid var(--chakra-colors-border-subtle);
					pointer-events: auto;
					transform-origin: center;
					transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
					word-wrap: break-word;
					// line-height: 1.4;
				}

				.tooltip-base.show {
					opacity: 1;
					transform: scale(1) translateY(0);
					animation: tooltipSlideIn 0.2s cubic-bezier(0.4, 0, 0.2, 1);
				}

				.tooltip-base.hide {
					opacity: 0;
					transform: scale(0.95) translateY(4px);
				}

				@keyframes tooltipSlideIn {
					0% {
						opacity: 0;
						transform: scale(0.9) translateY(8px);
					}
					100% {
						opacity: 1;
						transform: scale(1) translateY(0);
					}
				}

				/* Size variants */
				.tooltip-sm {
					padding: 6px 10px;
					font-size: 12px;
					max-width: 200px;
				}

				.tooltip-md {
					padding: 8px 12px;
					font-size: 14px;
					max-width: 280px;
				}

				.tooltip-lg {
					padding: 12px 16px;
					font-size: 16px;
					max-width: 320px;
				}

				/* Variant styles */
				.tooltip-default {
					background: var(--chakra-colors-bg-elevated);
					color: var(--chakra-colors-text-primary);
					border: 1px solid var(--chakra-colors-border-default);
				}

				.tooltip-primary {
					background: var(--chakra-colors-accent-gold);
					color: var(--chakra-colors-text-onAccent);
				}

				.tooltip-success {
					background: linear-gradient(135deg, #10b981 0%, #059669 100%);
					color: #ffffff;
				}

				.tooltip-warning {
					background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
					color: #ffffff;
				}

				.tooltip-error {
					background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
					color: #ffffff;
				}

				.tooltip-info {
					background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
					color: #ffffff;
				}

				.tooltip-dark {
					background: linear-gradient(135deg, #111827 0%, #000000 100%);
					color: #f9fafb;
					border: 1px solid #374151;
				}

				.tooltip-light {
					background: linear-gradient(135deg, #ffffff 0%, #f9fafb 100%);
					color: #1f2937;
					border: 1px solid #e5e7eb;
					box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
				}

				/* Arrow styles */
				.tooltip-arrow {
					position: absolute;
					width: 0;
					height: 0;
					border-style: solid;
					filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
				}

				.tooltip-arrow-top {
					bottom: -8px;
					left: 50%;
					transform: translateX(-50%);
					border-left: 8px solid transparent;
					border-right: 8px solid transparent;
					border-top: 8px solid;
				}

				.tooltip-arrow-bottom {
					top: -8px;
					left: 50%;
					transform: translateX(-50%);
					border-left: 8px solid transparent;
					border-right: 8px solid transparent;
					border-bottom: 8px solid;
				}

				.tooltip-arrow-left {
					right: -8px;
					top: 50%;
					transform: translateY(-50%);
					border-top: 8px solid transparent;
					border-bottom: 8px solid transparent;
					border-left: 8px solid;
				}

				.tooltip-arrow-right {
					left: -8px;
					top: 50%;
					transform: translateY(-50%);
					border-top: 8px solid transparent;
					border-bottom: 8px solid transparent;
					border-right: 8px solid;
				}

				/* Arrow colors for variants */
				.tooltip-default .tooltip-arrow-top {
					border-top-color: var(--chakra-colors-bg-elevated);
				}
				.tooltip-default .tooltip-arrow-bottom {
					border-bottom-color: var(--chakra-colors-bg-elevated);
				}
				.tooltip-default .tooltip-arrow-left {
					border-left-color: var(--chakra-colors-bg-elevated);
				}
				.tooltip-default .tooltip-arrow-right {
					border-right-color: var(--chakra-colors-bg-elevated);
				}

				.tooltip-primary .tooltip-arrow-top {
					border-top-color: var(--chakra-colors-accent-gold);
				}
				.tooltip-primary .tooltip-arrow-bottom {
					border-bottom-color: var(--chakra-colors-accent-gold);
				}
				.tooltip-primary .tooltip-arrow-left {
					border-left-color: var(--chakra-colors-accent-gold);
				}
				.tooltip-primary .tooltip-arrow-right {
					border-right-color: var(--chakra-colors-accent-gold);
				}

				.tooltip-success .tooltip-arrow-top {
					border-top-color: #10b981;
				}
				.tooltip-success .tooltip-arrow-bottom {
					border-bottom-color: #10b981;
				}
				.tooltip-success .tooltip-arrow-left {
					border-left-color: #10b981;
				}
				.tooltip-success .tooltip-arrow-right {
					border-right-color: #10b981;
				}

				.tooltip-warning .tooltip-arrow-top {
					border-top-color: #f59e0b;
				}
				.tooltip-warning .tooltip-arrow-bottom {
					border-bottom-color: #f59e0b;
				}
				.tooltip-warning .tooltip-arrow-left {
					border-left-color: #f59e0b;
				}
				.tooltip-warning .tooltip-arrow-right {
					border-right-color: #f59e0b;
				}

				.tooltip-error .tooltip-arrow-top {
					border-top-color: #ef4444;
				}
				.tooltip-error .tooltip-arrow-bottom {
					border-bottom-color: #ef4444;
				}
				.tooltip-error .tooltip-arrow-left {
					border-left-color: #ef4444;
				}
				.tooltip-error .tooltip-arrow-right {
					border-right-color: #ef4444;
				}

				.tooltip-info .tooltip-arrow-top {
					border-top-color: #06b6d4;
				}
				.tooltip-info .tooltip-arrow-bottom {
					border-bottom-color: #06b6d4;
				}
				.tooltip-info .tooltip-arrow-left {
					border-left-color: #06b6d4;
				}
				.tooltip-info .tooltip-arrow-right {
					border-right-color: #06b6d4;
				}

				.tooltip-dark .tooltip-arrow-top {
					border-top-color: #111827;
				}
				.tooltip-dark .tooltip-arrow-bottom {
					border-bottom-color: #111827;
				}
				.tooltip-dark .tooltip-arrow-left {
					border-left-color: #111827;
				}
				.tooltip-dark .tooltip-arrow-right {
					border-right-color: #111827;
				}

				.tooltip-light .tooltip-arrow-top {
					border-top-color: #ffffff;
				}
				.tooltip-light .tooltip-arrow-bottom {
					border-bottom-color: #ffffff;
				}
				.tooltip-light .tooltip-arrow-left {
					border-left-color: #ffffff;
				}
				.tooltip-light .tooltip-arrow-right {
					border-right-color: #ffffff;
				}

				/* Responsive design */
				@media (max-width: 768px) {
					.tooltip-base {
						max-width: 250px;
						font-size: 13px;
					}
					.tooltip-lg {
						max-width: 280px;
						font-size: 14px;
					}
				}

				/* Accessibility */
				@media (prefers-reduced-motion: reduce) {
					.tooltip-base {
						transition: opacity 0.15s ease;
					}
					.tooltip-base.show {
						animation: none;
					}
				}
				}
			`}</style>

			<div className={`tooltip-container ${fullWidth ? 'full-width' : ''}`}>
				<div
					ref={triggerRef}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
					onClick={handleClick}
					onFocus={handleFocus}
					onBlur={handleBlur}
					className={`tooltip-trigger ${disabled ? 'disabled' : ''} ${fullWidth ? 'full-width' : ''}`}
					tabIndex={effectiveTrigger === 'focus' ? 0 : -1}
					aria-describedby={isOpen ? 'tooltip' : undefined}
				>
					{children}
				</div>

				{isOpen && createPortal(
					<div
						ref={tooltipRef}
						onMouseEnter={handleTooltipMouseEnter}
						onMouseLeave={handleTooltipMouseLeave}
						className={`
							tooltip-base
							tooltip-${variant}
							tooltip-${size}
							${isOpen ? 'show' : 'hide'}
							${className}
						`}
						role='tooltip'
						id='tooltip'
						aria-hidden={!isOpen}
						{...props}
					>
						{label}
						{hasArrow && (
							<div
								className={`tooltip-arrow tooltip-arrow-${actualPlacement}`}
							/>
						)}
					</div>,
					document.body,
				)}
			</div>
		</>
	);
};

export default CustomTooltip;
